import { IconContext, WarningCircle, CheckCircle } from "@phosphor-icons/react";
import { pick } from "@styled-system/props";
import { Box, Flex, Spinner, Stack, SystemProps, Text } from "flicket-ui";
import { useContext } from "react";
import ReactTooltip from "react-tooltip";
import { Icon } from "~components";
import {
  CompetitionContext,
  CompetitionState,
} from "~features/competitions/CompetitionContext";
import { SocialLink } from "~features/events/components/PromoterShareCard";
import { CompetitionType } from "~graphql/typed-document-nodes";
import { AttributeNames } from "~telemetry/enums/AttributeNames";
import useCaptureClick from "~telemetry/traces/useCaptureClick";

interface SocialButtonCardProps extends SystemProps {
  eventId: string;
  socialLink: SocialLink;
  eventPhase: CompetitionType;
}

export const SocialButtonCard = ({
  socialLink,
  eventId,
  eventPhase,
  ...props
}: SocialButtonCardProps) => {
  const competitionContext = useContext(CompetitionContext);
  const competition = competitionContext?.competition;

  const competitionId = competition?.id;
  const captureClick = useCaptureClick({
    forwardToNest: true,
    eventId,
    attributes: {
      ...(competitionId
        ? { [AttributeNames.FLICKET_COMPETITION_ID]: competitionId }
        : {}),
      [AttributeNames.FLICKET_EVENT_PHASE]: eventPhase,
    },
  });

  return (
    <Stack
      direction={"horizontal"}
      gap={2}
      flex={{ _: undefined, md: `calc(50% - 8px)` }}
      alignItems={"center"}
      paddingX={3}
      paddingY={1}
      backgroundColor={"N100"}
      cursor={"pointer"}
      onClick={captureClick(() => {
        socialLink.handler();
        if (competition && socialLink.action !== "copy:link") {
          void competitionContext?.grantCompetitionReward(socialLink.action);
        }
      }, socialLink.analyticsEventName)}
      {...pick(props)}
    >
      <Stack alignItems={"center"} gap={1} flex={1}>
        <Icon icon={socialLink.icon} fontSize={40} />
        <Text variant="regular">{socialLink.label}</Text>
      </Stack>

      {competition && socialLink.numEntries && (
        <ItemStatus
          state={competitionContext.entries?.[socialLink.action]}
          entries={socialLink.numEntries}
        />
      )}
    </Stack>
  );
};

const ItemStatus = ({
  entries,
  state,
}: {
  entries: number;
  state: CompetitionState;
}) => {
  if (!state) {
    return (
      <Flex
        borderRadius={"full"}
        backgroundColor={"white"}
        width={"40px"}
        height={"40px"}
        alignItems={"center"}
        justifyContent={"center"}
      >
        <Text variant={"button.Bold"}>+{entries}</Text>
      </Flex>
    );
  }

  if (state.isSubmitting || entries > 10) {
    return <Spinner size={35} />;
  }

  return (
    <>
      {state.error && <ReactTooltip effect="solid" multiline={true} />}
      <IconContext.Provider value={{ size: 40, weight: "fill" }}>
        <Icon
          icon={state.error ? <WarningCircle /> : <CheckCircle />}
          data-tip={state.error}
          color={state.error ? "error" : "#63CE92"}
        />
      </IconContext.Provider>
    </>
  );
};
